import { showToast } from '../../../engine/toast';
import {
  adminListLegal, adminDeleteLegal,
  adminListFaq, adminDeleteFaq,
  adminListNews, adminDeleteNews, adminListSocial,
  listItems, type CmsFaq, type CmsLegal, type CmsNews,
} from '../admin-api';
import { escapeHtml, when, SMALL_BTN_CLS } from '../admin-ui';
import { openCmsEditor, type CmsKind } from '../modals/cms-editor';
import type { PaneCtx, PaneModule } from './types';
import { draftFromServer, socialAction, socialChange, socialHtml, socialKeyDown, socialPointerDown, socialSubmit } from './social';

type CmsTab = CmsKind | 'social';

const TABS: { id: CmsTab; label: string }[] = [
  { id: 'legal', label: 'Legal' },
  { id: 'faq', label: 'FAQ' },
  { id: 'news', label: 'News' },
  { id: 'social', label: 'Social' },
];

function stateChip(on: boolean): string {
  return `<span class="${on ? 'text-success' : 'text-text-dim'}">${on ? 'live' : 'hidden'}</span>`;
}

function actions(kind: CmsKind, id: string): string {
  return `
    <td class="py-2 text-right whitespace-nowrap">
      <button type="button" class="${SMALL_BTN_CLS}" data-action="cms-edit" data-kind="${kind}" data-id="${id}">Edit</button>
      <button type="button" class="${SMALL_BTN_CLS} ml-1" data-action="cms-delete" data-kind="${kind}" data-id="${id}">Delete</button>
    </td>`;
}

function rowsHtml(ctx: PaneCtx): string {
  const tab = ctx.state.cms.tab as CmsKind;
  if (tab === 'legal') {
    return ctx.state.cms.legal
      .map(
        (r: CmsLegal) => `
        <tr class="border-t border-border">
          <td class="py-2 pr-3 text-sm text-text">${escapeHtml(r.title)}</td>
          <td class="py-2 pr-3 text-[10px] text-text-dim">${escapeHtml(r.slug)}</td>
          <td class="py-2 pr-3 text-[10px]">${stateChip(r.active)}</td>
          ${actions('legal', r.id)}
        </tr>`,
      )
      .join('');
  }
  if (tab === 'faq') {
    return ctx.state.cms.faq
      .map(
        (r: CmsFaq) => `
        <tr class="border-t border-border">
          <td class="py-2 pr-3 text-sm text-text">${escapeHtml(r.question)}</td>
          <td class="py-2 pr-3 text-[10px] text-text-dim tabular-nums">${r.sortOrder}</td>
          <td class="py-2 pr-3 text-[10px]">${stateChip(r.active)}</td>
          ${actions('faq', r.id)}
        </tr>`,
      )
      .join('');
  }
  return ctx.state.cms.news
    .map(
      (r: CmsNews) => `
      <tr class="border-t border-border">
        <td class="py-2 pr-3 text-sm text-text">${escapeHtml(r.title)}</td>
        <td class="py-2 pr-3 text-[10px] text-text-dim">${escapeHtml(when(r.createdAt))}</td>
        <td class="py-2 pr-3 text-[10px]">${stateChip(r.published)}</td>
        ${actions('news', r.id)}
      </tr>`,
    )
    .join('');
}

const HEADS: Record<CmsKind, string[]> = {
  legal: ['Document', 'Slug', 'State'],
  faq: ['Question', 'Order', 'State'],
  news: ['Title', 'Created', 'State'],
};

export const cmsPane: PaneModule = {
  id: 'cms',
  label: 'Content',

  html(ctx) {
    const tab = ctx.state.cms.tab;
    const rows = tab === 'social' ? '' : rowsHtml(ctx);
    return `
      <section class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <h2 class="font-heading text-xs text-gold tracking-wide uppercase">Content</h2>
          ${tab === 'social' ? '' : `<button type="button" class="${SMALL_BTN_CLS}" data-action="cms-new" data-kind="${tab}">+ New</button>`}
        </div>
        <div class="flex gap-1">
          ${TABS.map(
            (t) => `
            <button type="button" data-action="cms-tab" data-kind="${t.id}"
              aria-current="${t.id === tab ? 'true' : 'false'}"
              class="min-h-11 px-3 text-[11px] uppercase tracking-[0.14em] border-b-2 touch-manipulation ${
                t.id === tab ? 'border-b-gold text-text' : 'border-b-transparent text-text-dim hover:text-text'
              }">${t.label}</button>`,
          ).join('')}
        </div>
        ${tab === 'social' ? socialHtml(ctx) : `<div class="glass-panel px-3 py-2 overflow-x-auto">
          <table class="w-full text-left">
            <thead>
              <tr class="text-[10px] text-text-dim uppercase tracking-wide">
                ${HEADS[tab as CmsKind].map((h) => `<th class="py-1 pr-3 font-normal">${h}</th>`).join('')}
                <th class="py-1 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>${rows || `<tr><td colspan="4" class="py-3 text-xs text-text-dim">Nothing here yet.</td></tr>`}</tbody>
          </table>
        </div>`}
        ${
          tab === 'legal'
            ? '<p class="text-[10px] text-text-dim leading-relaxed">Legal pages open from the site footer. Content is rich text and is sanitised before a player ever sees it.</p>'
            : ''
        }
        ${
          tab === 'faq'
            ? '<p class="text-[10px] text-text-dim leading-relaxed">These entries drive the FAQ on the landing page. With none published, the landing page falls back to its built-in copy.</p>'
            : ''
        }
      </section>`;
  },

  async load(ctx) {
    // All three lists, because switching tab must not re-fetch — the tab is a
    // local view over data already held.
    const [legal, faq, news, social] = await Promise.all([adminListLegal(), adminListFaq(), adminListNews(), adminListSocial()]);
    ctx.state.cms.legal = listItems(legal);
    ctx.state.cms.faq = listItems(faq);
    ctx.state.cms.news = listItems(news);
    ctx.state.cms.social = listItems(social);
    ctx.state.cms.socialDraft = draftFromServer(ctx.state.cms.social);
  },

  onPointerDown: socialPointerDown,
  onKeyDown: socialKeyDown,
  onChange: (el) => socialChange(el),
  onSubmit: (kind, form, ctx) => (kind === 'social' ? socialSubmit(form, ctx) : false),

  onAction(action, el, ctx) {
    if (socialAction(action, el, ctx)) return true;
    const kind = (el.dataset.kind ?? 'legal') as CmsKind;
    const id = el.dataset.id ?? '';

    if (action === 'cms-tab') {
      ctx.state.cms.tab = el.dataset.kind as CmsTab;
      ctx.repaint();
      return true;
    }
    if (action === 'cms-new') {
      openCmsEditor(ctx, kind, null);
      return true;
    }
    if (action === 'cms-edit') {
      const row =
        kind === 'legal'
          ? ctx.state.cms.legal.find((r) => r.id === id)
          : kind === 'faq'
            ? ctx.state.cms.faq.find((r) => r.id === id)
            : ctx.state.cms.news.find((r) => r.id === id);
      if (row) openCmsEditor(ctx, kind, row);
      return true;
    }
    if (action === 'cms-delete') {
      if (!window.confirm('Delete this entry? This cannot be undone.')) return true;
      void ctx.withBusy(async () => {
        if (kind === 'legal') await adminDeleteLegal(id);
        else if (kind === 'faq') await adminDeleteFaq(id);
        else await adminDeleteNews(id);
        showToast('Deleted.', 'success');
        await ctx.reload('cms');
      }, 'Could not delete that entry.');
      return true;
    }
    return false;
  },
};
